import { useCallback, useReducer } from 'react';

export type PasteSummaryState = {
  successfulCells: number;
  failedCells: number;
  pendingCells: number;
  hasPasteEnded: boolean;
};

type PasteSummaryAction =
  | { type: 'paste-started' }
  | { type: 'paste-ended' }
  | { type: 'cell-success' }
  | { type: 'cell-failure' }
  | { type: 'cell-pending' }
  | { type: 'reset' };

const initialState: PasteSummaryState = {
  successfulCells: 0,
  failedCells: 0,
  pendingCells: 0,
  hasPasteEnded: false,
};

function pasteSummaryReducer(state: PasteSummaryState, action: PasteSummaryAction): PasteSummaryState {
  switch (action.type) {
    case 'paste-started':
      return {
        ...initialState,
      };
    case 'paste-ended':
      return {
        ...state,
        hasPasteEnded: true,
      };
    case 'cell-success':
      return {
        ...state,
        successfulCells: state.successfulCells + 1,
        pendingCells: Math.max(0, state.pendingCells - 1),
      };
    case 'cell-failure':
      return {
        ...state,
        failedCells: state.failedCells + 1,
        pendingCells: Math.max(0, state.pendingCells - 1),
      };
    case 'cell-pending':
      return {
        ...state,
        pendingCells: state.pendingCells + 1,
      };
    case 'reset':
      return {
        ...initialState,
      };
    default:
      return state;
  }
}

export function usePasteSummary() {
  const [state, dispatch] = useReducer(pasteSummaryReducer, initialState);
  const startPaste = useCallback(() => {
    dispatch({ type: 'paste-started' });
  }, []);

  const endPaste = useCallback(() => {
    dispatch({ type: 'paste-ended' });
  }, []);

  const registerPendingCellPaste = useCallback(() => {
    dispatch({ type: 'cell-pending' });
  }, []);

  const registerSuccessfulCellPaste = useCallback(() => {
    dispatch({ type: 'cell-success' });
  }, []);

  const registerFailedCellPaste = useCallback(() => {
    dispatch({ type: 'cell-failure' });
  }, []);

  const resetPasteSummaryState = useCallback(() => {
    dispatch({ type: 'reset' });
  }, []);

  return {
    state,
    startPaste,
    endPaste,
    registerPendingCellPaste,
    registerSuccessfulCellPaste,
    registerFailedCellPaste,
    resetPasteSummaryState,
  };
}
